import { useState, useCallback, useMemo } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useApiMutation } from '../hooks/useApiMutation'
import { useToast } from '../components/ToastContainer'
import { apiFetch, webUrl } from '../lib/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { FileUpload } from '../ui/FileUpload'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'
import DOMPurify from 'dompurify'
import styles from './SharedIcons.module.css'

// ── 타입 ──

interface SharedAsset {
  id: string
  type: string
  name: string
  slug: string
  category: string
  tags: string[]
  description: string | null
  s3Key: string | null
  fileUrl: string | null
  fileSize: number | null
  mimeType: string | null
  inlineContent: string | null
  metadata: Record<string, unknown>
  sortOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface AssetsResponse {
  assets: SharedAsset[]
}

// ── 상수 ──

const CATEGORY_OPTIONS = [
  { value: 'wedding', label: '웨딩' },
  { value: 'ui', label: 'UI' },
  { value: 'contact', label: '연락처' },
  { value: 'social', label: 'SNS' },
  { value: 'map', label: '지도/교통' },
  { value: 'other', label: '기타' },
]

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  ...CATEGORY_OPTIONS,
]

const CATEGORY_BADGE_COLORS: Record<string, 'blue' | 'green' | 'red' | 'yellow'> = {
  wedding: 'red',
  ui: 'blue',
  contact: 'green',
  social: 'yellow',
  map: 'blue',
  other: 'blue',
}

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
)

function sanitizeSvg(svg: string): string {
  return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } })
}

function getSourceLabel(asset: SharedAsset): string {
  if (asset.inlineContent && !asset.s3Key) return 'SVG코드'
  if (asset.mimeType === 'image/svg+xml') return 'SVG파일'
  return '이미지'
}

function getSourceBadgeColor(asset: SharedAsset): 'blue' | 'green' | 'yellow' {
  if (asset.inlineContent && !asset.s3Key) return 'yellow'
  if (asset.mimeType === 'image/svg+xml') return 'green'
  return 'blue'
}

// ── 메인 컴포넌트 ──

export function SharedIcons() {
  const { addToast } = useToast()

  const { data, isLoading, refetch } = useApiFetch<AssetsResponse>(
    '/api/admin/shared-assets?type=icon&includeInactive=true',
  )
  const assetsList = data?.assets ?? []

  const [categoryFilter, setCategoryFilter] = useState('')
  const filteredAssets = useMemo(() => {
    if (!categoryFilter) return assetsList
    return assetsList.filter((a) => a.category === categoryFilter)
  }, [assetsList, categoryFilter])

  // 파일 업로드
  const [uploading, setUploading] = useState(false)
  const [uploadKey, setUploadKey] = useState(0)

  // SVG 코드 직접 입력 모달
  const [showSvgInput, setShowSvgInput] = useState(false)
  const [svgCode, setSvgCode] = useState('')
  const [svgName, setSvgName] = useState('')

  // 편집 모달
  const [editingAsset, setEditingAsset] = useState<SharedAsset | null>(null)
  const [formName, setFormName] = useState('')
  const [formSlug, setFormSlug] = useState('')
  const [formCategory, setFormCategory] = useState('other')
  const [formTags, setFormTags] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formSortOrder, setFormSortOrder] = useState(0)
  const [formInlineContent, setFormInlineContent] = useState('')

  const createMut = useApiMutation<SharedAsset, Record<string, unknown>>('/api/admin/shared-assets', 'POST')
  const updateMut = useApiMutation<SharedAsset, Record<string, unknown>>('/api/admin/shared-assets', 'PUT')
  const toggleMut = useApiMutation<unknown, Record<string, unknown>>('/api/admin/shared-assets', 'PATCH')

  // 파일 업로드 → 자동 등록
  const handleUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        await apiFetch<SharedAsset>(
          '/api/admin/shared-assets/upload?type=icon',
          { method: 'POST', body: formData },
        )
      }
      addToast({ type: 'success', message: `${files.length}개 아이콘이 등록되었습니다` })
      setUploadKey((k) => k + 1)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '업로드 실패' })
    } finally {
      setUploading(false)
    }
  }, [addToast, refetch])

  // SVG 코드 직접 등록
  const handleSvgCodeSave = useCallback(async () => {
    if (!svgCode.trim() || !svgName.trim()) return
    const slug = svgName.toLowerCase().replace(/[^a-z0-9가-힣\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 50) || 'icon'
    const result = await createMut.trigger({
      type: 'icon',
      name: svgName,
      slug,
      category: 'other',
      inlineContent: svgCode,
      metadata: { renderMode: 'inline' },
    })
    if (result) {
      addToast({ type: 'success', message: 'SVG 아이콘이 등록되었습니다' })
      setShowSvgInput(false)
      setSvgCode('')
      setSvgName('')
      refetch()
    }
  }, [svgCode, svgName, createMut, addToast, refetch])

  // 편집
  const openEdit = useCallback((asset: SharedAsset) => {
    setEditingAsset(asset)
    setFormName(asset.name)
    setFormSlug(asset.slug)
    setFormCategory(asset.category)
    setFormTags((asset.tags ?? []).join(', '))
    setFormDescription(asset.description ?? '')
    setFormSortOrder(asset.sortOrder)
    setFormInlineContent(asset.inlineContent ?? '')
  }, [])

  const handleSave = useCallback(async () => {
    if (!editingAsset) return
    const tags = formTags.split(',').map(t => t.trim()).filter(Boolean)
    const result = await updateMut.trigger({
      name: formName,
      slug: formSlug,
      category: formCategory,
      tags,
      description: formDescription || null,
      sortOrder: formSortOrder,
      inlineContent: formInlineContent || null,
    }, { pathOverride: `/api/admin/shared-assets/${editingAsset.id}` })
    if (result) {
      addToast({ type: 'success', message: '수정되었습니다' })
      setEditingAsset(null)
      refetch()
    }
  }, [editingAsset, formName, formSlug, formCategory, formTags, formDescription, formSortOrder, formInlineContent, updateMut, addToast, refetch])

  const handleToggle = useCallback(async (row: SharedAsset, _newValue: boolean) => {
    const result = await toggleMut.trigger({}, { pathOverride: `/api/admin/shared-assets/${row.id}/toggle` })
    if (result === null) throw new Error('토글 변경에 실패했습니다')
    refetch()
  }, [toggleMut, refetch])

  const handleDelete = useCallback(async (row: SharedAsset) => {
    await apiFetch(`/api/admin/shared-assets/${row.id}`, { method: 'DELETE' })
    refetch()
  }, [refetch])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner />
      </div>
    )
  }

  const columns: TableColumn<SharedAsset>[] = [
    {
      key: 'preview',
      label: '',
      width: '50px',
      render: (row) => {
        if (row.inlineContent) {
          return <div className={styles.iconPreview} dangerouslySetInnerHTML={{ __html: sanitizeSvg(row.inlineContent) }} />
        }
        if (row.fileUrl) {
          return <div className={styles.iconPreview}><img src={row.fileUrl} alt={row.name} /></div>
        }
        return null
      },
    },
    {
      key: 'name',
      label: '이름',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.slug}</div>
        </>
      ),
    },
    {
      key: 'category',
      label: '카테고리',
      width: '100px',
      render: (row) => (
        <Badge color={CATEGORY_BADGE_COLORS[row.category] ?? 'blue'}>
          {CATEGORY_LABELS[row.category] ?? row.category}
        </Badge>
      ),
    },
    {
      key: 'source',
      label: '소스',
      width: '90px',
      render: (row) => (
        <Badge color={getSourceBadgeColor(row)}>{getSourceLabel(row)}</Badge>
      ),
    },
    {
      key: 'url',
      label: 'URL',
      width: '60px',
      render: (row) => (
        <button
          type="button"
          className={styles.copyUrlBtn}
          onClick={() => {
            navigator.clipboard.writeText(webUrl(`/assets/icon/${row.slug}`))
            addToast({ type: 'success', message: 'URL이 복사되었습니다' })
          }}
          aria-label="URL 복사"
        >
          📋
        </button>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>아이콘 에셋</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} options={CATEGORY_FILTER_OPTIONS} size="sm" />
          <Button size="sm" variant="secondary" onClick={() => setShowSvgInput(true)}>SVG 코드 입력</Button>
        </div>
      </div>

      {/* 파일 업로드 영역 */}
      <div style={{ marginBottom: '24px' }}>
        <FileUpload
          key={uploadKey}
          accept=".svg,.png,.webp"
          placeholder=".svg, .png, .webp 파일을 드래그하거나 클릭 (자동 등록)"
          onChange={handleUpload}
          loading={uploading}
          multiple
        />
      </div>

      <AdminTable<SharedAsset>
        columns={columns}
        data={filteredAssets}
        rowKey={(row) => row.id}
        emptyMessage="등록된 아이콘이 없습니다"
        hoverable
        toggles={[{ label: '활성', onToggle: handleToggle, getValue: (row) => row.isActive }]}
        actions={[{ label: '편집', onClick: openEdit }]}
        delete={{
          message: (row) => `"${row.name}" 아이콘을 삭제하시겠습니까?`,
          onDelete: handleDelete,
          successMessage: '삭제되었습니다',
        }}
      />

      {/* SVG 코드 직접 입력 모달 */}
      <Modal open={showSvgInput} onClose={() => setShowSvgInput(false)} title="SVG 코드로 아이콘 등록" size="lg">
        <div className={styles.formGrid}>
          <div className={styles.formFull}>
            <Input label="이름" value={svgName} onChange={(e) => setSvgName(e.target.value)} placeholder="하트 아이콘" />
          </div>
          <div className={styles.formFull}>
            <Textarea
              label="SVG 코드"
              value={svgCode}
              onChange={(e) => setSvgCode(e.target.value)}
              placeholder='<svg viewBox="0 0 24 24">...</svg>'
              rows={8}
              className={styles.svgInput}
            />
          </div>
          {svgCode && (
            <div className={styles.formFull}>
              <div className={styles.modalPreview}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgCode) }} />
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="secondary" onClick={() => setShowSvgInput(false)}>취소</Button>
          <Button onClick={handleSvgCodeSave} disabled={!svgCode.trim() || !svgName.trim() || createMut.isLoading}>등록</Button>
        </div>
      </Modal>

      {/* 편집 모달 */}
      <Modal open={!!editingAsset} onClose={() => setEditingAsset(null)} title="아이콘 편집" size="lg">
        <div className={styles.formGrid}>
          {(editingAsset?.inlineContent || editingAsset?.fileUrl) && (
            <div className={styles.formFull}>
              <div className={styles.modalPreview}>
                {editingAsset.inlineContent ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeSvg(editingAsset.inlineContent) }} />
                ) : editingAsset.fileUrl ? (
                  <img src={editingAsset.fileUrl} alt={editingAsset.name} />
                ) : null}
              </div>
            </div>
          )}
          <Input label="이름" value={formName} onChange={(e) => setFormName(e.target.value)} />
          <Input label="Slug" value={formSlug} onChange={(e) => setFormSlug(e.target.value)} />
          <Select label="카테고리" value={formCategory} onChange={(e) => setFormCategory(e.target.value)} options={CATEGORY_OPTIONS} />
          <Input label="정렬 순서" type="number" value={String(formSortOrder)} onChange={(e) => setFormSortOrder(Number(e.target.value))} />
          <div className={styles.formFull}>
            <Input label="태그 (쉼표 구분)" value={formTags} onChange={(e) => setFormTags(e.target.value)} />
          </div>
          <div className={styles.formFull}>
            <Textarea label="설명" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} />
          </div>
          {editingAsset?.inlineContent != null && (
            <div className={styles.formFull}>
              <Textarea
                label="SVG 코드"
                value={formInlineContent}
                onChange={(e) => setFormInlineContent(e.target.value)}
                rows={6}
                className={styles.svgInput}
              />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="secondary" onClick={() => setEditingAsset(null)}>취소</Button>
          <Button onClick={handleSave} disabled={!formName || !formSlug || updateMut.isLoading}>수정</Button>
        </div>
      </Modal>
    </div>
  )
}
